import { memo, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppShell,
  Group,
  Avatar,
  Menu,
  ActionIcon,
  useMantineColorScheme,
  Box,
  UnstyledButton,
} from '@mantine/core';
import {
  IconDashboard,
  IconUsers,
  IconUser,
  IconLogout,
  IconSun,
  IconMoon,
  IconMenu2,
  IconHome,
  IconEdit,
  IconEditOff,
} from '@tabler/icons-react';
import { useAuthStore } from '../store/authSlice';
import { useAccess } from '../access/AccessContext';
import { useEditMode } from '../contexts/EditModeContext';
import { useNavigation } from '../contexts/NavigationContext';
import { PluginRegistry } from '../plugins';

interface NavButtonProps {
  icon: React.ComponentType<any>;
  label: string;
  active: boolean;
  onClick: () => void;
}

const NavButton = memo(function NavButton({ icon: Icon, active, onClick }: NavButtonProps) {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px 4px',
        borderRadius: 8,
        backgroundColor: active ? 'var(--mantine-color-blue-light)' : 'transparent',
        color: active ? 'var(--mantine-color-blue-filled)' : 'var(--mantine-color-gray-6)',
        transition: 'all 0.2s',
      }}
    >
      <Icon size={26} stroke={1.5} />
    </UnstyledButton>
  );
});

export const MobileLayout = memo(function MobileLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const { isAdmin } = useAccess();
  const { isEditMode, toggleEditMode } = useEditMode();
  const { resetHistory } = useNavigation();

  // Get plugin for current path
  const currentPlugin = PluginRegistry.getPluginForPath(location.pathname);
  const pluginNavigation = currentPlugin?.navigationItems || [];

  
  // Bottom navigation items - use plugin navigation when in plugin routes
  const bottomNavigation = useMemo(() => {
    if (currentPlugin && pluginNavigation.length > 0) {
      return pluginNavigation;
    }
    
    // Default navigation for core routes
    const items = [
      { label: t('navigation.dashboard'), icon: IconDashboard, path: '/' },
    ];
    
    if (isAdmin) {
      items.push({ label: t('navigation.users'), icon: IconUsers, path: '/users' });
    }
    
    items.push({ label: t('navigation.profile'), icon: IconUser, path: '/profile' });
    items.push({ label: t('navigation.more'), icon: IconMenu2, path: '/more' });
    
    return items;
  }, [t, isAdmin, currentPlugin, pluginNavigation]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <AppShell
      header={{ height: 56 }}
      footer={{ height: 64 }}
      padding={0}
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <ActionIcon
            variant="subtle"
            size="lg"
            onClick={resetHistory}
            title="На главную"
          >
            <IconHome size={24} />
          </ActionIcon>

          <Group gap="xs">
            {isAdmin && (
              <ActionIcon
                variant={isEditMode ? 'filled' : 'subtle'}
                color={isEditMode ? 'orange' : 'gray'}
                onClick={toggleEditMode}
                size="md"
                title={isEditMode ? 'Выключить режим редактора' : 'Режим редактора прав'}
              >
                {isEditMode ? <IconEditOff size={18} /> : <IconEdit size={18} />}
              </ActionIcon>
            )}

            <ActionIcon
              variant="subtle"
              onClick={() => toggleColorScheme()}
              size="md"
            >
              {colorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
            </ActionIcon>

            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <Avatar
                  size="sm"
                  style={{ cursor: 'pointer' }}
                  radius="xl"
                  color="blue"
                >
                  {user?.login?.charAt(0).toUpperCase()}
                </Avatar>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>{user?.login}</Menu.Label>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconLogout size={16} />}
                  onClick={handleLogout}
                  color="red"
                >
                  {t('auth.logout')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Main
        style={{
          height: 'calc(100vh - 56px - 64px)',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {isEditMode && (
          <Box
            style={{
              backgroundColor: 'var(--mantine-color-orange-6)',
              color: 'white',
              padding: '4px 16px',
              fontSize: 12,
              textAlign: 'center',
            }}
          >
            🔧 Режим редактора прав — кликайте на элементы для настройки
          </Box>
        )}
        <Box
          p="md"
          style={{
            minHeight: '100%',
            paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
          }}
        >
          <Outlet />
        </Box>
      </AppShell.Main>

      <AppShell.Footer>
        <Group
          h="100%"
          gap={0}
          style={{
            borderTop: '1px solid var(--mantine-color-gray-3)',
            backgroundColor: 'var(--mantine-color-body)',
          }}
        >
          {bottomNavigation.map((item) => (
            <NavButton
              key={item.path}
              icon={item.icon}
              label={item.label}
              active={isActive(item.path)}
              onClick={() => navigate(item.path)}
            />
          ))}
        </Group>
      </AppShell.Footer>
    </AppShell>
  );
});

export default MobileLayout;
